import type {
    MyPlan,
    PlanMeal,
    PlanShoppingPayload,
    PlanShoppingResult,
    SelectedPlanMeal,
} from '@/services/planShopping';
import { fromLocalDateStr } from '@/lib/date';

/**
 * Pure helpers for "Shop for your plan" (#330). Kept out of the page so the
 * range, overlap and payload rules can be read (and tested) on their own.
 */

/** Today's calendar date in South Africa, YYYY-MM-DD, whatever the device thinks. */
export function todaySastDateStr(now: Date = new Date()): string {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}

/** "Thu 22 Oct" */
export function formatShortDate(date: string): string {
    return fromLocalDateStr(date).toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}

/** "Thursday 22 October" */
export function formatLongDate(date: string): string {
    return fromLocalDateStr(date).toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
    });
}

/** The picker page. */
export const SHOP_FOR_PLAN_PATH = '/shopping/plan';

/** One list's page. */
export function shoppingListPath(listId: string): string {
    return `/shopping-lists/${encodeURIComponent(listId)}`;
}

/** The picker with "Add to an existing list" preselected on this list. */
export function addDaysToListPath(listId: string): string {
    return `${SHOP_FOR_PLAN_PATH}?list=${encodeURIComponent(listId)}`;
}

export const isShoppable = (meal: PlanMeal) => meal.hasIngredients;

export interface DayRange {
    from: number;
    to: number;
}

export interface QuickRange extends DayRange {
    id: 'next7' | 'first7' | 'whole';
    label: string;
}

/**
 * Before day one there is no "next 7 days" that differs from Day 1 to 7, so it
 * is only offered while the plan is running.
 */
export function quickRanges(plan: MyPlan): QuickRange[] {
    const n = plan.duration;
    const ranges: QuickRange[] = [];
    if (plan.phase === 'IN_PROGRESS') {
        const from = Math.min(Math.max(plan.currentDayNumber, 1), n);
        ranges.push({ id: 'next7', label: 'Next 7 days', from, to: Math.min(from + 6, n) });
    }
    ranges.push({
        id: 'first7',
        label: n >= 7 ? 'Day 1 to 7' : `Day 1 to ${n}`,
        from: 1,
        to: Math.min(7, n),
    });
    ranges.push({ id: 'whole', label: 'Whole plan', from: 1, to: n });
    return ranges;
}

export function defaultRange(plan: MyPlan): DayRange {
    // Running plan: the next 7 days. Upcoming or finished: Day 1 to 7.
    const [first] = quickRanges(plan);
    return { from: first.from, to: first.to };
}

/**
 * Up to 7 days starting the day after a list's last date, for "Add days" from
 * that list. null when the list has no range or already runs to the plan's end.
 */
export function rangeAfterList(plan: MyPlan, listEndDate?: string | null): DayRange | null {
    if (!listEndDate) return null;
    const end = listEndDate.slice(0, 10);
    const next = plan.days.find((d) => d.date > end);
    if (!next) return null;
    return { from: next.dayNumber, to: Math.min(next.dayNumber + 6, plan.duration) };
}

/** Calendar dates covered by a list's stored range, inclusive. */
export function datesBetween(start?: string | null, end?: string | null): Set<string> {
    const out = new Set<string>();
    if (!start || !end) return out;
    const from = fromLocalDateStr(start.slice(0, 10));
    const to = fromLocalDateStr(end.slice(0, 10));
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        out.add(`${y}-${m}-${day}`);
    }
    return out;
}

/**
 * The meals ticked by default for a range: every meal that has something to
 * buy, minus the days already on the list being added to.
 */
export function defaultSelection(
    plan: MyPlan,
    range: DayRange,
    alreadyOnListDates: Set<string>,
): Set<string> {
    const keys = new Set<string>();
    for (const day of plan.days) {
        if (day.dayNumber < range.from || day.dayNumber > range.to) continue;
        if (alreadyOnListDates.has(day.date)) continue;
        for (const meal of day.meals) {
            if (isShoppable(meal)) keys.add(meal.key);
        }
    }
    return keys;
}

/** [6, 7, 9] -> "Days 6 to 7 and 9"; [6] -> "Day 6". */
export function describeDays(dayNumbers: number[]): string {
    const sorted = [...new Set(dayNumbers)].sort((a, b) => a - b);
    if (sorted.length === 0) return '';
    const runs: string[] = [];
    let start = sorted[0];
    let prev = sorted[0];
    for (const n of sorted.slice(1).concat(Number.NaN)) {
        if (n === prev + 1) {
            prev = n;
            continue;
        }
        runs.push(start === prev ? `${start}` : `${start} to ${prev}`);
        start = n;
        prev = n;
    }
    const joined =
        runs.length === 1
            ? runs[0]
            : `${runs.slice(0, -1).join(', ')} and ${runs[runs.length - 1]}`;
    return `${sorted.length === 1 ? 'Day' : 'Days'} ${joined}`;
}

/**
 * "Days 1 to 7, <plan>", or "Days 1 to 7 (2), <plan>" for the second list of
 * those days: the counter sits after the days so it stays visible however the
 * name is cut off. The backend builds the same strings (plan-list-name.ts in
 * programs) to keep the name in step when days are added to the list later,
 * so the two must not drift apart.
 */
export function defaultListName(planName: string, dayNumbers: number[], n = 1): string {
    if (dayNumbers.length === 0) return planName;
    const from = Math.min(...dayNumbers);
    const to = Math.max(...dayNumbers);
    const days = from === to ? `Day ${from}` : `Days ${from} to ${to}`;
    return `${days}${n > 1 ? ` (${n})` : ''}, ${planName}`;
}

/**
 * The first of defaultListName(..., 1), (..., 2)... that none of the client's
 * lists is called, ignoring case (#376). Two lists both called "Days 1 to 7,
 * 14-Day Detox Challenge" could not be told apart on the lists page.
 */
export function uniqueListName(
    planName: string,
    dayNumbers: number[],
    takenNames: Iterable<string>,
): string {
    const taken = new Set([...takenNames].map((name) => name.trim().toLowerCase()));
    for (let n = 1; ; n++) {
        const candidate = defaultListName(planName, dayNumbers, n);
        if (!taken.has(candidate.toLowerCase()) || dayNumbers.length === 0) return candidate;
    }
}

export interface Selection {
    meals: PlanMeal[];
    dayNumbers: number[];
}

/** The ticked meals, in plan order, with the days they fall on. */
export function selectedMeals(plan: MyPlan, keys: Set<string>): Selection {
    const meals: PlanMeal[] = [];
    const dayNumbers: number[] = [];
    for (const day of plan.days) {
        const ticked = day.meals.filter((m) => isShoppable(m) && keys.has(m.key));
        if (ticked.length > 0) dayNumbers.push(day.dayNumber);
        meals.push(...ticked);
    }
    return { meals, dayNumbers };
}

/**
 * Servings to shop for (#395). The old "From My Menu" tab allowed whole
 * servings from 1 to 20 per meal; the picker keeps those bounds.
 */
export const SERVINGS_MIN = 1;
export const SERVINGS_MAX = 20;

/** The quick "Cooking for" choices. A bigger household edits a meal instead. */
export const COOKING_FOR_OPTIONS = [1, 2, 3, 4, 5, 6] as const;

/**
 * One step of a meal's stepper. Moves to the next whole serving, so a coach's
 * 1.5 goes to 1 or 2, and stays within SERVINGS_MIN to SERVINGS_MAX.
 */
export function stepServings(value: number, direction: 1 | -1): number {
    const next = direction > 0 ? Math.floor(value) + 1 : Math.ceil(value) - 1;
    return Math.min(SERVINGS_MAX, Math.max(SERVINGS_MIN, next));
}

/**
 * Where a meal's servings start: the recipe's own yield ("Recipe makes 6"
 * starts at 6), so a client shops for the whole recipe and edits down from
 * there. A meal with no recipe yield (a free-text custom meal) falls back to
 * the menu's servings.
 */
export function defaultServings(meal: PlanMeal): number {
    const yieldServings = meal.recipe?.servings;
    return yieldServings && yieldServings > 0 ? yieldServings : meal.servings;
}

/**
 * What a meal will be shopped for: the client's own number for that meal,
 * else "Cooking for N" (one serving per person), else the recipe's yield.
 */
export function servingsFor(
    meal: PlanMeal,
    cookingFor: number | null,
    overrides: ReadonlyMap<string, number>,
): number {
    return overrides.get(meal.key) ?? cookingFor ?? defaultServings(meal);
}

/** 2 -> "2 servings", 1 -> "1 serving", 1.5 -> "1.5 servings". */
export function describeServings(n: number): string {
    const value = Number.isInteger(n) ? `${n}` : `${Math.round(n * 100) / 100}`;
    return `${value} ${n === 1 ? 'serving' : 'servings'}`;
}

export function buildPayload(options: {
    meals: PlanMeal[];
    /**
     * Servings to shop for each meal. Only a number that differs from the
     * menu's servings is sent, because the server uses the menu's otherwise.
     * The picker starts meals at the recipe's yield, so that is usually sent.
     */
    servings?: (meal: PlanMeal) => number;
    destination: 'new' | 'existing';
    name: string;
    shoppingListId?: string;
    /** The user ticked meals on days already on the target list. */
    includeMealsAlreadyOnList: boolean;
}): PlanShoppingPayload {
    const { meals, destination } = options;
    const dates = meals.map((m) => m.date).sort();
    const selected: SelectedPlanMeal[] = meals.map((m) => {
        const entry: SelectedPlanMeal =
            m.type === 'custom' && m.customMealId
                ? { date: m.date, customMealId: m.customMealId }
                : { date: m.date, programMealId: m.programMealId };
        const servings = options.servings?.(m);
        if (servings !== undefined && servings !== m.servings) entry.servings = servings;
        return entry;
    });
    const payload: PlanShoppingPayload = {
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        selectedMeals: selected,
    };
    if (destination === 'existing' && options.shoppingListId) {
        payload.shoppingListId = options.shoppingListId;
        if (options.includeMealsAlreadyOnList) payload.includeMealsAlreadyOnList = true;
    } else if (options.name.trim()) {
        payload.name = options.name.trim();
    }
    return payload;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What happened, in plain words, for the toast after a list is made or added to. */
export function describeResult(result: PlanShoppingResult): string[] {
    const lines: string[] = [];
    const merge = result.merge;
    if (!merge || merge.mode === 'created') {
        const count = merge?.added ?? result.items.length;
        lines.push(`${result.name} is ready with ${plural(count, 'item', 'items')}.`);
    } else {
        const parts: string[] = [];
        if (merge.added > 0) parts.push(plural(merge.added, 'new item', 'new items'));
        if (merge.increased > 0)
            parts.push(`${plural(merge.increased, 'item', 'items')} topped up`);
        lines.push(
            parts.length > 0
                ? `Added to ${result.name}: ${parts.join(', ')}.`
                : `Nothing new to add to ${result.name}.`,
        );
        if (merge.unticked > 0) {
            lines.push(
                `${plural(merge.unticked, 'ticked item needs', 'ticked items need')} more, so ${merge.unticked === 1 ? 'it is' : 'they are'} unticked again.`,
            );
        }
    }
    const skipped = merge?.skipped?.length ?? 0;
    if (skipped > 0) {
        lines.push(
            `${plural(skipped, 'item you changed', 'items you changed')} yourself ${skipped === 1 ? 'was' : 'were'} left as ${skipped === 1 ? 'it was' : 'they were'}.`,
        );
    }
    // Warnings (RANGE_OVERLAP, MEALS_NOT_ON_MENU) and unlisted meals are not
    // repeated here: they go to the list page in the route state, which shows
    // them on the list itself.
    return lines;
}

/**
 * Route state the list page reads after a list is made or added to. Same
 * shape as ShoppingListRouteState on the list page (#330).
 */
export interface PlanShoppingRouteState {
    unlistedMeals?: { date: string; mealType?: string; name: string }[];
    warnings?: { code: string; message: string }[];
}

export function routeStateFor(result: PlanShoppingResult): PlanShoppingRouteState {
    return {
        unlistedMeals: result.unlistedMeals ?? [],
        warnings: (result.warnings ?? []).map((w) => ({ code: w.code, message: w.message })),
    };
}
